'use client';

import { useCallback, useState } from 'react';

import axiosInstance, { endpoints } from 'src/lib/axios';

import type { IAdress, OrganisationsTyp } from 'src/types/nya/common';

// ----------------------------------------------------------------------

/**
 * Verifiering av en organisation mot Bolagsverket, delad av varje yta som skapar organisationer.
 *
 * <p>Namn och organisationsnummer skrevs tidigare fritt i tre olika formulär. En användare
 * registrerade då "Kråkan" på Bolund Ink AB:s organisationsnummer, och det riktiga bolaget gick
 * sedan inte att lägga till — numret var upptaget av ett påhittat namn. Backend avgör numera vem
 * organisationen är, men regeln måste synas i gränssnittet också, och på ett ställe: tre kopior
 * blir två kvarglömda bakvägar.
 *
 * <p>Hooken äger fyra saker: numret, vad uppslaget gav, om användaren uttryckligen bekräftat att
 * organisationen saknas hos Bolagsverket, och därmed om formuläret får skickas.
 */

/**
 * `EJ_KÖRD` — ingen har tryckt Verifiera än. Inget får skapas.
 * `VERIFIERAD` — Bolagsverket svarade. Namn och form är låsta.
 * `OKÄNT_NUMMER` — numret finns inte i registret. Samfälligheter ligger hos Lantmäteriet,
 *   ideella föreningar hos Skatteverket, och en enskild firma har inget eget organisationsnummer.
 *   Den vägen vidare kräver en uttrycklig bekräftelse.
 * `FEL` — vi nådde inte Bolagsverket. Till skillnad från `OKÄNT_NUMMER` säger det ingenting om
 *   organisationen, så det öppnar ingen väg vidare: annars hade en timeout varit genvägen.
 */
export type Verifieringsstatus = 'EJ_KÖRD' | 'VERIFIERAD' | 'OKÄNT_NUMMER' | 'FEL';

export type VerifieradeUppgifter = {
    organisationsNamn: string;
    organisationsTyp: OrganisationsTyp | null;
    adress: IAdress | null;
    /** Användarens egna styrelseuppdrag hos Bolagsverket, när personnummer finns på kontot. */
    personensAndraEngagemang: { organisationsNamn: string; organisationsNummer: string }[];
};

/** Tio siffror, med eller utan bindestreck. Samma krav som backend ställer. */
export function ärGiltigtOrganisationsnummer(nummer: string): boolean {
    return /^\d{10}$/.test(nummer.replace(/\D/g, ''));
}

export function formateraOrganisationsnummer(nummer: string): string {
    const siffror = nummer.replace(/\D/g, '');
    if (siffror.length !== 10) return nummer.trim();
    return `${siffror.slice(0, 6)}-${siffror.slice(6)}`;
}

export function useOrganisationsVerifiering() {
    const [organisationsnummer, setOrganisationsnummer] = useState('');
    const [status, setStatus] = useState<Verifieringsstatus>('EJ_KÖRD');
    const [uppgifter, setUppgifter] = useState<VerifieradeUppgifter | null>(null);
    const [okäntNummerBekräftat, setOkäntNummerBekräftat] = useState(false);
    const [verifierar, setVerifierar] = useState(false);
    const [felmeddelande, setFelmeddelande] = useState<string | null>(null);

    const återställ = useCallback(() => {
        setStatus('EJ_KÖRD');
        setUppgifter(null);
        setOkäntNummerBekräftat(false);
        setFelmeddelande(null);
    }, []);

    /**
     * Varje tangenttryckning i nummerfältet nollställer verifieringen. Annars kan någon verifiera
     * ett nummer, ändra det, och skapa organisationen på ett godkännande som gällde något annat.
     */
    const ändraOrganisationsnummer = useCallback(
        (värde: string) => {
            setOrganisationsnummer(värde);
            återställ();
        },
        [återställ]
    );

    const verifiera = useCallback(async (): Promise<Verifieringsstatus> => {
        const siffror = organisationsnummer.replace(/\D/g, '');
        if (!ärGiltigtOrganisationsnummer(organisationsnummer)) {
            setStatus('EJ_KÖRD');
            setFelmeddelande('Organisationsnumret ska bestå av tio siffror.');
            return 'EJ_KÖRD';
        }

        setVerifierar(true);
        setFelmeddelande(null);
        try {
            const svar = await axiosInstance.get(endpoints.organisation.verifiera, {
                params: { query: siffror },
            });
            const data = svar.data;
            setUppgifter({
                organisationsNamn: data.organisationsNamn,
                organisationsTyp: data.organisationsTyp ?? null,
                adress: data.adress ?? null,
                personensAndraEngagemang: data.personensAndraEngagemang ?? [],
            });
            setOkäntNummerBekräftat(false);
            setStatus('VERIFIERAD');
            return 'VERIFIERAD';
        } catch (fel) {
            // 404 betyder att Bolagsverket svarade och inte kände numret — ett besked om
            // organisationen. Allt annat betyder att vi inte fick något besked alls, och det är
            // inte samma sak.
            const statuskod = (fel as { response?: { status?: number } })?.response?.status;
            setUppgifter(null);
            if (statuskod === 404) {
                setStatus('OKÄNT_NUMMER');
                return 'OKÄNT_NUMMER';
            }
            setStatus('FEL');
            setFelmeddelande(
                'Vi kunde inte nå Bolagsverket just nu. Försök igen om en stund.'
            );
            return 'FEL';
        } finally {
            setVerifierar(false);
        }
    }, [organisationsnummer]);

    /** Namnet kommer från registret och ska inte gå att skriva över. */
    const namnetÄrLåst = status === 'VERIFIERAD';

    /**
     * Grinden. Ett okänt nummer räcker inte i sig — bekräftelsen ska vara ett eget, medvetet val,
     * inte nästa knapp i ordningen.
     */
    const fårSkapas = status === 'VERIFIERAD' || (status === 'OKÄNT_NUMMER' && okäntNummerBekräftat);

    return {
        organisationsnummer,
        ändraOrganisationsnummer,
        setOrganisationsnummer,
        status,
        uppgifter,
        okäntNummerBekräftat,
        setOkäntNummerBekräftat,
        verifierar,
        felmeddelande,
        verifiera,
        återställ,
        namnetÄrLåst,
        fårSkapas,
    };
}
